// Round 12, Part 4: assemble the authoritative, pre-computed ledger figures
// that get passed into the Interrogate/summary prompt. Every number here is
// produced by the same deterministic libs the UI renders (integer-cent
// financialTotals + exact dateMath) — the LLM receives the ANSWERS and is told
// to use them verbatim, so it narrates and never does the arithmetic itself
// (the fix for the $250.01-class summary error).
//
// Pure function, no React, no AI — unit-tested via node --test.

import type { RecordFact } from "../types";
import { parseAmount } from "./factMeta";
import { formatCents, toCents } from "./costsMath";
import {
  reconcileSignedCents,
  BUCKET_LABELS,
  type LedgerBucket,
} from "./financialTotals";
import { buildLedger, type LedgerOverrides } from "./ledger";
import { daysBetween, elapsedVsPromised, formatDayCount } from "./dateMath";

export interface LedgerRecordMeta {
  subject?: string | null;
  record_date?: string | null;
}

export function buildLedgerPromptBlock(
  facts: RecordFact[],
  recordMeta: Map<string, LedgerRecordMeta>,
  overrides: LedgerOverrides,
  nowISO: string,
): string {
  const lines: string[] = [];

  // --- Financial totals (verify-or-suppress) — THE shared ledger, so the
  //     figures the model cites match the Overview net and the Summary tab. ---
  const ledger = buildLedger(facts, overrides);
  if (ledger.net.asserted) {
    const n = ledger.net;
    lines.push("FINANCIALS (deterministic, integer-cent):");
    lines.push(`- Total costs incurred: ${formatCents(n.costCents)}`);
    lines.push(`- Total recoveries / amounts received: ${formatCents(n.recoveryCents)}`);
    lines.push(`- Net (costs − recoveries): ${formatCents(n.netCents)}`);
    for (const b of n.bucketSubtotals) {
      if (b.bucket === "cost" || b.bucket === "recovery") continue;
      lines.push(`- ${BUCKET_LABELS[b.bucket as LedgerBucket]} subtotal (reference, not in net): ${formatCents(b.cents)}`);
    }
  } else if (ledger.items.length > 0) {
    lines.push(
      `FINANCIALS: case-wide net NOT computed — ${ledger.net.reason} The individual verified amounts are shown in the app; do not sum them yourself.`,
    );
  }
  if (ledger.excluded.length > 0) {
    lines.push(
      `- Excluded from all totals: ${ledger.excluded.length} unverified/unparseable amount(s) (listed separately, never counted).`,
    );
  }

  // --- Reconciliations (intra-record exact fold) ---
  const byRecord = new Map<string, RecordFact[]>();
  for (const f of facts) {
    if (f.fact_type !== "amount" || !f.verified) continue;
    if (parseAmount(f.amount) === 0) continue;
    const g = byRecord.get(f.record_id);
    if (g) g.push(f);
    else byRecord.set(f.record_id, [f]);
  }
  const reconLines: string[] = [];
  for (const [recordId, group] of byRecord) {
    if (group.length < 3) continue;
    const cents = group.map((f) => toCents(parseAmount(f.amount)));
    const r = reconcileSignedCents(
      cents,
      group.map((f) => f.summary ?? ""),
    );
    const title = recordMeta.get(recordId)?.subject?.trim() || "a record";
    if (r.reconciles && r.totalIndex !== null) {
      reconLines.push(
        `- "${title}": components reconcile exactly to the stated total ${formatCents(cents[r.totalIndex]!)}.`,
      );
    } else if (r.discrepancy) {
      reconLines.push(
        `- "${title}": a stated total does NOT reconcile with its components — flag as a discrepancy, do not invent a reconciled figure.`,
      );
    }
  }
  if (reconLines.length > 0) {
    lines.push("RECONCILIATIONS:");
    lines.push(...reconLines);
  }

  // --- Durations / deadlines (exact UTC day math) ---
  const dated = facts
    .map((f) => f.date)
    .filter((d): d is string => !!d)
    .sort();
  if (dated.length >= 2) {
    const span = daysBetween(dated[0]!, dated[dated.length - 1]!);
    if (span !== null)
      lines.push(
        `TIMELINE: case spans ${formatDayCount(span)} (${dated[0]!.slice(0, 10)} → ${dated[dated.length - 1]!.slice(0, 10)}).`,
      );
  }
  const deadlineLines: string[] = [];
  for (const f of facts) {
    if (!f.deadline) continue;
    const e = elapsedVsPromised(f.deadline, nowISO);
    if (!e) continue;
    const phrase = e.dueToday
      ? "due today"
      : e.overdue
        ? `${formatDayCount(e.days)} overdue`
        : `${formatDayCount(e.days)} remaining`;
    deadlineLines.push(
      `- ${(f.summary ?? "deadline").trim()}: promised ${f.deadline.slice(0, 10)} — ${phrase} as of ${nowISO.slice(0, 10)}.`,
    );
  }
  if (deadlineLines.length > 0) {
    lines.push("DEADLINES:");
    lines.push(...deadlineLines.slice(0, 20));
  }

  return lines.join("\n").trim();
}
